import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const Profile = () => {
    const navigate = useNavigate();
    const [expandedSection, setExpandedSection] = useState(null);
    const [favorites, setFavorites] = useState([]); // Initialize as an empty array
    const [error, setError] = useState(null);
    const [searchableData, setSearchableData] = useState([]);
    const [passportFile, setPassportFile] = useState(null);
    const [uploadStatus, setUploadStatus] = useState('');

    const loadData = useCallback(async () => {
        try {
            const universityResponse = await fetch('/data/data.csv');
            const universityText = await universityResponse.text();
            const universityData = parseCSV(universityText);
    
            const costResponse = await fetch('/data/cost.csv');
            const costText = await costResponse.text();
            const costData = parseCostCSV(costText);
    
            const combinedData = universityData.map(course => ({
                ...course,
                price: costData[course.id]
                    ? `From $${(Number(costData[course.id]) / 52).toFixed(2)} USD per week`
                    : 'No cost available',
            }));
    
            setSearchableData(combinedData); // Set searchable data
        } catch (error) {
            console.error('Error loading data:', error);
        }
    }, []);
    
    useEffect(() => {
        loadData(); // Load data when component mounts
    }, [loadData]);
    const parseCSV = (csvText) => {
        const lines = csvText.split('\n');
        const headers = lines[0].split(',').map(header => header.trim());
        return lines.slice(1).map(line => {
          const values = line.split(',').map(value => value.trim().replace(/^"|"$/g, ''));
          const course = {};
          headers.forEach((header, index) => {
            course[header] = values[index];
          });
          return {
            id: course.UNITID,
            title: course.INSTNM,
            subtitle: `${course.ADDR}, ${course.CITY}, ${course.STABBR} ${course.ZIP}`,
            location: course.WEBADDR,
            imgSrc: `https://getimage.wordpad.workers.dev/?id=${course.UNITID}&keyword=${course.INSTNM}`,
            starSrc: './assets/img/icons/star.svg',
            rating: 4.5,
            price: 'Loading...',
            age: 'from 14 years',
            detailsPage: 'details-page',
            state: course.STABBR
          };
        });
      };
    
      const parseCostCSV = (csvText) => {
        const lines = csvText.split('\n');
        const headers = lines[0].split(',').map(header => header.trim());
        const tuitionData = {};
    
        lines.slice(1).forEach(line => {
          const values = line.split(',').map(value => value.trim().replace(/^"|"$/g, ''));
          const unitIdIndex = headers.indexOf('UNITID');
          const tuitionIndex = headers.indexOf('TUITION2');
          if (unitIdIndex !== -1 && tuitionIndex !== -1) {
            const unitId = values[unitIdIndex];
            const tuition = values[tuitionIndex];
            tuitionData[unitId] = tuition;
          }
        });
    
        return tuitionData;
      };

    
    const fetchFavorites = async (username) => {
        console.log(`Fetching favorites for username: ${username}`);
        try {
            const response = await fetch(
                `https://b101favorites.wordpad.workers.dev/?username=${username}`,
                { method: 'GET' }
            );
            if (response.ok) {
                const favoriteIds = await response.json();
                console.log('Favorites fetched successfully:', favoriteIds);
    
                // Wait for searchableData to load, then filter
                if (Array.isArray(favoriteIds)) {
                    const favoriteCourses = searchableData.filter(course => favoriteIds.includes(course.id));
                    setFavorites(favoriteCourses); // Set the filtered favorites
                    console.log('Filtered favorite courses:', favoriteCourses);
                } else {
                    console.log('Unexpected data format, defaulting to empty array.');
                    setFavorites([]);
                }
            } else {
                console.error('Failed to fetch favorites. Status:', response.status);
                setError('Failed to fetch favorites.');
            }
        } catch (err) {
            console.error('Error occurred while fetching favorites:', err);
            setError('Failed to fetch favorites.');
        }
    };
    
    useEffect(() => {
        if (searchableData.length > 0) {
            const username = localStorage.getItem('username');
            if (username) {
                fetchFavorites(username); // Trigger fetch after searchableData is loaded
            }
        }
    }, [searchableData]);   
    
    const handleCourseSelection = (course) => {
        console.log("Button clicked", course);
        navigate(`/fetch/${course.id}`);
      };    
    
// Add this inside useEffect after fetchFavorites
useEffect(() => {
    if (favorites.length > 0 || error) {
        const favoriteSection = document.getElementById('favorites-section');
        if (favoriteSection) {
            favoriteSection.innerHTML = `
                <h3>Your Favorite Universities</h3>
                ${error ? `<p>${error}</p>` : ''}
                ${favorites.length > 0 ? `
                    <ul>
                        ${favorites.map(favorite => `<li>${favorite}</li>`).join('')}
                    </ul>
                ` : '<p>No favorites found.</p>'}
            `;
        }
    }
}, [favorites, error]);

    const handlePassportUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        // Add file validation
        const maxSize = 5 * 1024 * 1024; // 5MB
        const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
        
        if (file.size > maxSize) {
            setUploadStatus('File is too large. Maximum size is 5MB.');
            return;
        }
        
        if (!allowedTypes.includes(file.type)) {
            setUploadStatus('Invalid file type. Please upload a JPG, PNG, or PDF file.');
            return;
        }
        
        setPassportFile(file);
        setUploadStatus('Uploading...');
        
        try {
            // Create FormData
            const formData = new FormData();
            formData.append('file', file);
            formData.append('fileName', `passports/${Date.now()}-${file.name}`);

            // Note the /upload endpoint here
            const uploadResponse = await fetch('https://upload-file.wordpad.workers.dev/upload', {
                method: 'POST',
                body: formData
            });

            if (!uploadResponse.ok) {
                const errorText = await uploadResponse.text();
                throw new Error(errorText);
            }

            const { url } = await uploadResponse.json();
            setUploadStatus('Passport uploaded successfully!');
            console.log('File uploaded to:', url);
        } catch (error) {
            console.error('Error uploading passport:', error);
            setUploadStatus(`Upload failed: ${error.message}`);
        }
    };

    const toggleExpansion = (section) => {
        setExpandedSection(prevSection => (prevSection === section ? null : section));
    };

    const sections = [
        {
            title: 'Research Universities and Programs',
            id: 'research',
            description: 'Identify U.S. universities that offer the programs you’re interested in. Check their admission requirements, tuition fees, and campus life.'
        },
        {
            title: 'Prepare for Standardized Tests',
            id: 'prepare-tests',
            description: 'Take necessary exams like TOEFL/IELTS for English proficiency, and SAT/ACT or GRE/GMAT, depending on your chosen program.'
        },
        {
            title: 'Apply to Universities',
            id: 'apply',
            description: 'Submit applications to your selected universities, ensuring you meet all deadlines. Include required documents like transcripts, letters of recommendation, and personal statements.'
        },
        {
            title: 'Obtain Acceptance Letters',
            id: 'acceptance',
            description: 'Wait for admission decisions and receive acceptance letters from universities that admit you.'
        },
        {
            title: 'Apply for a Student Visa (F-1/M-1)',
            id: 'visa-application',
            description: 'Use the Form I-20 provided by your university to apply for a student visa at a U.S. embassy or consulate.'
        },
        {
            title: 'Pay SEVIS Fee',
            id: 'sevis',
            description: 'Pay the SEVIS I-901 fee before your visa interview.'
        },
        {
            title: 'Attend Visa Interview',
            id: 'visa-interview',
            description: 'Schedule and attend a visa interview at the U.S. embassy or consulate. Bring required documents, including your I-20, financial evidence, and proof of ties to your home country.'
        },
        {
            title: 'Receive Student Visa',
            id: 'student-visa',
            description: 'If approved, receive your visa and prepare for travel to the U.S.'
        },
        {
            title: 'Arrange Accommodation and Finances',
            id: 'accommodation',
            description: 'Secure housing either on-campus or off-campus and ensure you have adequate financial resources for tuition and living expenses.'
        },
        {
            title: 'Travel to the USA',
            id: 'travel',
            description: 'Arrive in the U.S. up to 30 days before your program start date. Go through customs and immigration at your port of entry.'
        },
        {
            title: 'Attend Orientation and Register for Classes',
            id: 'orientation',
            description: 'Participate in the university’s orientation program for international students and register for your classes.'
        },
        {
            title: 'Maintain Visa Status',
            id: 'maintain-visa',
            description: 'Stay compliant with the terms of your student visa by maintaining full-time enrollment and not engaging in unauthorized employment.'
        },
    ];

    return (
        <main className="main-profile">
            <div className="profile-grid">
                <div className="profile-grid__container container">
                    <section className="button button--secondary banner-support">
                        <a href="al-powered-support.html">
                            Receive AI-Powered Support and Connection with Student Ambassadors for just $50 a month
                        </a>
                    </section>
                    <aside className="profile-menu">
                        <div className="profile-menu__container">
                            <div className="profile-menu__header">
                                <div className="profile-menu__photo">
                                    <img src="./assets/img/icons/account-avatar.svg" alt="Account Avatar" />
                                </div>
                                <span className="profile-menu__name">Wilson W</span>
                            </div>
                            <div className="profile-menu__fullness">
                                <div className="profile-menu__fullness-progress">
                                    <progress value="20" max="100"></progress>
                                    <span className="profile-menu__fullness-value">20%</span>
                                </div>
                                <p className="profile-menu__fullness-message">Complete your profile to be visible to the universities</p>
                            </div>
                            <button className="button button--outline profile-menu__update">Update my account</button>
                            <nav className="profile-menu__nav">
                                {sections.map(section => (
                                    <a 
                                        key={section.id}
                                        className="profile-menu__nav-link"
                                        href={`#${section.id}`}
                                        onClick={() => toggleExpansion(section.id)}
                                    >
                                        {section.title}
                                    </a>
                                ))}
                            </nav>
                        </div>
                    </aside>
                    <div className="profile-grid__content">
                        <section className="button button--secondary banner-support">
                            <a href="al-powered-support.html">
                                Receive AI-Powered Support and Connection with Student Ambassadors for just $50 a month
                            </a>
                        </section>
                        <div className="application-wrap">
                            {sections.map(section => (
                                <section id={section.id} key={section.id} className="application">
                                    <h2 
                                        className="application__title js-close"
                                        onClick={() => toggleExpansion(section.id)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {section.title}
                                    </h2>
                                    {expandedSection === section.id && (
                                        <div>
                                            <p>{section.description}</p>
                                            {expandedSection === 'research' && (
                                                <div>
                                                    <h3>Your Favorite Universities</h3>
                                                    {error && <p>{error}</p>}
                                                    {Array.isArray(favorites) && favorites.length > 0 ? (
                                                        <div className="courses-cards">
                                                            {favorites.map((course, index) => (
                                                                <article key={index} className="courses-cards-item courses-card">
                                                                    <div className="courses-card__img-wrap">
                                                                        <img src={course.imgSrc} alt="Course Image" />
                                                                    </div>
                                                                    <div className="courses-card__content">
                                                                        <div className="courses-card__head">
                                                                            <h2 className="courses-card__title">{course.title}</h2>
                                                                            <h3 className="courses-card__subtitle">{course.subtitle}</h3>
                                                                        </div>
                                                                        <div className="courses-card__rating rating" data-rating={course.rating}>
                                                                            {[...Array(Math.floor(course.rating))].map((_, idx) => (
                                                                                <img key={idx} src={course.starSrc} alt="Star" />
                                                                            ))}
                                                                        </div>
                                                                        <span className="courses-card__location">{course.location}</span>
                                                                        <span className="courses-card__price">{course.price}</span>
                                                                        <span className="courses-card__age">{course.age}</span>
                                                                        <button
                                                                            className="button button--primary courses-card__button"
                                                                            onClick={() => handleCourseSelection(course)}
                                                                        >
                                                                            View Details
                                                                        </button>
                                                                    </div>
                                                                </article>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p>No favorites found.</p>
                                                    )}
                                                </div>
                                            )}
                                            {expandedSection === 'apply' && (
                                                <div className="passport-upload-section">
                                                    <h3>Upload Passport</h3>
                                                    <div className="upload-container">
                                                        <input
                                                            type="file"
                                                            accept=".pdf,.jpg,.jpeg,.png"
                                                            onChange={handlePassportUpload}
                                                            className="file-input"
                                                        />
                                                        {uploadStatus && <p className="upload-status">{uploadStatus}</p>}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </section>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default Profile;
